const YOUTUBE_ID_PATTERN = /^[a-zA-Z0-9_-]{11}$/;

export type YoutubeVideo = {
  id: string;
  isShorts: boolean;
};

const isYoutubeHost = (hostname: string) => {
  const host = hostname.replace(/^www\./, "");
  return (
    host === "youtube.com" ||
    host === "m.youtube.com" ||
    host === "youtu.be" ||
    host === "music.youtube.com" ||
    host === "youtube-nocookie.com"
  );
};

export const parseYoutubeUrl = (input?: string): YoutubeVideo | null => {
  if (!input) return null;

  const trimmed = input.trim();
  if (YOUTUBE_ID_PATTERN.test(trimmed)) {
    return { id: trimmed, isShorts: false };
  }

  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  if (!isYoutubeHost(url.hostname)) return null;

  const host = url.hostname.replace(/^www\./, "");
  const parts = url.pathname.split("/").filter(Boolean);

  if (host === "youtu.be") {
    const id = parts[0];
    return id && YOUTUBE_ID_PATTERN.test(id) ? { id, isShorts: false } : null;
  }

  if (parts[0] === "shorts" && parts[1] && YOUTUBE_ID_PATTERN.test(parts[1])) {
    return { id: parts[1], isShorts: true };
  }

  if (parts[0] === "embed" && parts[1] && YOUTUBE_ID_PATTERN.test(parts[1])) {
    return { id: parts[1], isShorts: false };
  }

  if (parts[0] === "live" && parts[1] && YOUTUBE_ID_PATTERN.test(parts[1])) {
    return { id: parts[1], isShorts: false };
  }

  const videoId = url.searchParams.get("v");
  if (videoId && YOUTUBE_ID_PATTERN.test(videoId)) {
    return { id: videoId, isShorts: false };
  }

  return null;
};

export const buildYoutubeEmbedHtml = (input: string): string => {
  const video = parseYoutubeUrl(input);
  if (!video) return "";

  const wrapperStyle = video.isShorts
    ? "position:relative;max-width:360px;width:100%;margin:16px auto;aspect-ratio:9/16;"
    : "position:relative;width:100%;max-width:640px;margin:16px 0;aspect-ratio:16/9;";

  const title = video.isShorts ? "YouTube Shorts" : "YouTube video";

  return `<div class="youtube-embed${
    video.isShorts ? " youtube-embed--shorts" : ""
  }" style="${wrapperStyle}"><iframe src="https://www.youtube.com/embed/${
    video.id
  }" title="${title}" style="position:absolute;top:0;left:0;width:100%;height:100%;border:0;" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowfullscreen></iframe></div>`;
};

export const isPlainYoutubeUrl = (input: string) => {
  const trimmed = input.trim();
  return Boolean(trimmed) && !trimmed.includes("<") && Boolean(parseYoutubeUrl(trimmed));
};
